(function () {
  'use strict';

  // ── What this does ─────────────────────────────────────────────────
  // Teeinblue GenAI products upload the raw selected photo straight to the
  // GenAI endpoint without showing Teeinblue's native crop dialog. This
  // script inserts a free-crop step in between: it intercepts the file
  // selection on the Teeinblue photo input, lets the customer crop, then
  // feeds the cropped image back into the same input so Teeinblue uploads
  // the cropped version (same DataTransfer technique as hb-ai-generator.js).
  //
  // Loaded only on products tagged `photo-cropper` (see layout/theme.liquid).

  // The Teeinblue photo file input lives inside .tee-photo-input within a
  // .tee-field--photo block. Match that so we never touch unrelated inputs.
  var FIELD_SELECTOR = '.tee-field--photo';
  // Cap the longest exported edge. 4000px keeps near-original quality (most
  // phone photos are 3000–4000px) while staying under iOS Safari's canvas
  // area limit (~16.7M px), above which getCroppedCanvas returns null.
  var MAX_OUTPUT_DIM = 4000;
  var JPEG_QUALITY = 0.95; // re-encode quality for cropped JPEGs

  var processed = new WeakSet(); // files that already passed through the cropper
  var _i18nEl = null;
  var _busy = false; // a cropper modal is currently open

  // ── i18n (reads data-* from #hb-cropper-i18n, German fallbacks) ─────
  var _fallback = {
    title: 'Foto zuschneiden',
    hint: 'Ziehe den Rahmen, um den Bildausschnitt zu wählen.',
    confirm: 'Übernehmen',
    cancel: 'Abbrechen'
  };
  var _keyToAttr = { title: 'title', hint: 'hint', confirm: 'confirm', cancel: 'cancel' };

  function t(key) {
    if (!_i18nEl) _i18nEl = document.querySelector('#hb-cropper-i18n');
    var attr = _keyToAttr[key];
    if (attr && _i18nEl) {
      var v = _i18nEl.getAttribute('data-' + attr);
      if (v) return v;
    }
    return _fallback[key] || key;
  }

  // ── Helpers ─────────────────────────────────────────────────────────
  function isTargetInput(el) {
    return !!(el && el.matches && el.matches('input[type="file"]') && el.closest(FIELD_SELECTOR));
  }

  // Output format: keep PNG (transparency), otherwise JPEG (smaller payload).
  function outputType(file) {
    return file && file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  }

  function outputName(file, type) {
    var base = (file && file.name ? file.name : 'photo').replace(/\.[^.]+$/, '');
    return base + (type === 'image/png' ? '.png' : '.jpg');
  }

  // ── Modal ───────────────────────────────────────────────────────────
  function buildModal(imgSrc) {
    var overlay = document.createElement('div');
    overlay.id = 'hb-cropper-overlay';
    overlay.innerHTML =
      '<div class="hb-cropper-dialog" role="dialog" aria-modal="true">' +
        '<div class="hb-cropper-title">' + t('title') + '</div>' +
        '<div class="hb-cropper-stage"><img id="hb-cropper-img" alt=""></div>' +
        '<div class="hb-cropper-hint">' + t('hint') + '</div>' +
        '<div class="hb-cropper-actions">' +
          '<button type="button" class="hb-cropper-btn hb-cropper-btn--ghost" id="hb-cropper-cancel">' + t('cancel') + '</button>' +
          '<button type="button" class="hb-cropper-btn hb-cropper-btn--primary" id="hb-cropper-confirm">' + t('confirm') + '</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);
    overlay.querySelector('#hb-cropper-img').src = imgSrc;
    return overlay;
  }

  function teardown(overlay, cropper) {
    if (cropper) { try { cropper.destroy(); } catch (e) {} }
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
    _busy = false;
  }

  // Fail open: hand the original file straight to Teeinblue (used when the
  // cropper can't run, so a glitch never blocks the customer from buying).
  function passThrough(file, input) {
    processed.add(file);
    var dt = new DataTransfer();
    dt.items.add(file);
    input.files = dt.files;
    _busy = false;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ── Core: open cropper for a freshly selected file ──────────────────
  function openCropper(file, input) {
    _busy = true;

    var reader = new FileReader();
    reader.onerror = function () { passThrough(file, input); };
    reader.onload = function (ev) {
     try {
      var overlay = buildModal(ev.target.result);
      var img = overlay.querySelector('#hb-cropper-img');
      var type = outputType(file);

      var cropper = new window.Cropper(img, {
        viewMode: 1,
        // Start at 80% so the frame sits inside the photo with a margin —
        // much easier to grab and drag the edges, especially on mobile.
        autoCropArea: 0.8,
        dragMode: 'move',
        background: false,
        responsive: true,
        movable: true,
        zoomable: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        minCropBoxWidth: 40,
        minCropBoxHeight: 40,
        // free crop — no aspectRatio lock
        checkOrientation: true
      });

      overlay.querySelector('#hb-cropper-cancel').addEventListener('click', function () {
        // Clear the selection so Teeinblue has nothing to upload.
        input.value = '';
        teardown(overlay, cropper);
      });

      overlay.querySelector('#hb-cropper-confirm').addEventListener('click', function () {
        var canvas = cropper.getCroppedCanvas({
          maxWidth: MAX_OUTPUT_DIM,
          maxHeight: MAX_OUTPUT_DIM,
          imageSmoothingQuality: 'high',
          fillColor: type === 'image/png' ? 'transparent' : '#fff'
        });
        // If the canvas couldn't be produced (e.g. exceeds a browser limit),
        // send the original rather than dropping the upload.
        if (!canvas) { teardown(overlay, cropper); passThrough(file, input); return; }

        canvas.toBlob(function (blob) {
          if (!blob) { teardown(overlay, cropper); passThrough(file, input); return; }
          var croppedFile = new File([blob], outputName(file, type), {
            type: type,
            lastModified: Date.now()
          });
          processed.add(croppedFile); // let the re-dispatched change pass through

          var dt = new DataTransfer();
          dt.items.add(croppedFile);
          input.files = dt.files;

          teardown(overlay, cropper);

          // Hand the cropped file to Teeinblue.
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }, type, type === 'image/jpeg' ? JPEG_QUALITY : undefined);
      });
     } catch (err) {
      // Anything unexpected (Cropper init, DOM) → don't trap the customer.
      passThrough(file, input);
     }
    };
    reader.readAsDataURL(file);
  }

  // ── Interception (capture phase, so we run before Teeinblue) ────────
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!isTargetInput(input)) return;

    var file = input.files && input.files[0];
    if (!file) return;
    if (processed.has(file)) return;            // already cropped → let it through
    if (!/^image\//.test(file.type)) return;    // non-image safety → let it through

    // Fail open: if the cropper library didn't load, never block the upload.
    if (typeof window.Cropper === 'undefined') return;
    if (_busy) return;

    // Stop Teeinblue from consuming the raw file; we'll re-dispatch the cropped one.
    e.stopImmediatePropagation();
    e.preventDefault();

    try {
      openCropper(file, input);
    } catch (err) {
      passThrough(file, input);
    }
  }, true);
})();
